import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Users, ChevronDown, X as XIcon, Search } from 'lucide-react';

/**
 * CustomerFilter — multi-select popover for filtering by Customer.
 *
 * Mirrors the ReferenceFilter pattern so it feels native. Options are the
 * distinct customers present in the current data set; the caller owns
 * ``value`` (array of customer ids) and ``onChange``.
 *
 * Props:
 *   options       {id, name}[]  distinct customers to choose from
 *   value         string[]      currently selected customer ids
 *   onChange      (next: string[]) => void
 *   counts        Map<id, number>  optional per-customer match counts
 *   testIdPrefix  string        data-testid namespace
 */
export function CustomerFilter({
  options = [],
  value = [],
  onChange,
  counts,
  testIdPrefix = 'customer-filter',
}) {
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const sortedOptions = useMemo(() => {
    const seen = new Map();
    for (const o of (options || [])) {
      if (o && o.id && !seen.has(o.id)) seen.set(o.id, o.name || 'Unnamed');
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return sortedOptions;
    return sortedOptions.filter((c) => (c.name || '').toLowerCase().includes(term));
  }, [sortedOptions, q]);

  const toggle = (id) => {
    const cur = new Set(value || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    onChange(Array.from(cur));
  };

  const active = (value || []).length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
            active
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
              : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
          }`}
          data-testid={`${testIdPrefix}-trigger`}
          title="Filter by Customer"
        >
          <Users size={13} />
          <span>{active ? `Customer · ${(value || []).length} selected` : 'All customers'}</span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start" data-testid={`${testIdPrefix}-content`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Customer</div>
          {active && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        {sortedOptions.length > 8 && (
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customers…"
              className="h-8 pl-7 text-[12.5px]"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        )}
        <div className="max-h-60 overflow-y-auto grid grid-cols-1 gap-1">
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-500 py-2 text-center">No customers.</div>
          )}
          {filtered.map((c) => {
            const isOn = selectedSet.has(c.id);
            const count = counts ? (counts.get(c.id) || 0) : null;
            return (
              <label
                key={c.id}
                className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 cursor-pointer border ${
                  isOn ? 'bg-emerald-50 border-emerald-200' : 'border-transparent hover:bg-slate-50'
                }`}
                data-testid={`${testIdPrefix}-option-${c.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox
                    checked={isOn}
                    onCheckedChange={() => toggle(c.id)}
                    data-testid={`${testIdPrefix}-option-${c.id}-checkbox`}
                  />
                  <span className="text-sm text-slate-800 truncate">{c.name}</span>
                </div>
                {count != null && (
                  <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">{count}</span>
                )}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * CustomerFilterChips — removable chips for the selected customers.
 */
export function CustomerFilterChips({ value = [], nameById = {}, onChange, testIdPrefix = 'customer-filter-chip' }) {
  if (!Array.isArray(value) || value.length === 0) return null;
  const remove = (id) => onChange((value || []).filter((x) => x !== id));
  return (
    <>
      {value.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 border-emerald-200"
          data-testid={`${testIdPrefix}-${id}`}
        >
          <span className="text-[10px] uppercase tracking-wide opacity-70">Customer</span>
          <span className="truncate max-w-[140px]">{nameById[id] || 'Unnamed'}</span>
          <button
            type="button"
            onClick={() => remove(id)}
            className="ml-0.5 inline-flex items-center rounded-full hover:bg-black/10 p-0.5 transition-colors"
            aria-label="Remove customer filter"
          >
            <XIcon size={11} />
          </button>
        </span>
      ))}
    </>
  );
}
